import AutorenewIcon from '@mui/icons-material/Autorenew';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Popover,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDepletionsTrend, type DateRange } from '../api/depletions';
import { fmt6PkNumber } from '../lib/volume';
import { colors } from '../theme';
import {
  excelSheet,
  isoDate,
  num,
  type ExcelColumn,
  type ExportFilter,
} from '../util/excel-export';
import { SectionExplainer } from './SectionExplainer';
import { useSectionExport } from '../hooks/useSectionExport';

interface DepletionsTrendChartProps {
  range?: DateRange;
  // When set, the chart is scoped to a single product's full history —
  // used per-SKU inside the Product Performance deep-dive.
  productId?: number;
  title?: string;
  subtitle?: string;
  // Slim variant for embedding: drops the summary tiles, the active-
  // accounts sub-chart, and the reader's guide, keeping just the filter
  // bar + the year-over-year volume overlay.
  compact?: boolean;
  // Registers the section's Excel export. Defaults to true only for the
  // top-level (portfolio) chart so a per-product copy embedded in another
  // section never hijacks that section's export.
  exportable?: boolean;
}

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Per-year line colors, assigned newest-year-first so the current year
// reads in the bold brand indigo and older years step back into calmer,
// distinct hues. Distinct hues (not a single fading ramp) keep three or
// four overlaid years easy to tell apart at a glance.
const YEAR_COLORS = ['#4f46e5', '#0d9488', '#d97706', '#64748b', '#db2777', '#0891b2'];

function monthYearLabel(year: string, monthNum: number): string {
  return `${MONTHS[monthNum - 1] ?? '?'} ${year}`;
}

// One pivoted row per calendar month (Jan..Dec). Each present year adds
// a numeric key holding that month's 9L volume / active-account count,
// so the chart can draw one line per year on a shared month axis.
interface MonthRow {
  monthNum: number;
  month: string;
  vol: Record<string, number | null>;
  accts: Record<string, number | null>;
}

export function DepletionsTrendChart({
  range,
  productId,
  title,
  subtitle,
  compact = false,
  exportable = productId === undefined,
}: DepletionsTrendChartProps) {
  const { data, isLoading, error } = useDepletionsTrend(range, 'month', productId);
  const heading = title ?? 'Monthly Depletion Volume';
  const caption =
    subtitle ??
    '6-pack equivalent volume by month, one line per year — compare the same month across years';

  // Years present in the data, newest first (drives color + legend order).
  const years = useMemo(() => {
    const set = new Set<string>();
    for (const p of data?.points ?? []) {
      const [y] = p.period.split('-');
      if (y) set.add(y);
    }
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [data]);

  const colorByYear = useMemo(() => {
    const map = new Map<string, string>();
    years.forEach((y, i) => map.set(y, YEAR_COLORS[i % YEAR_COLORS.length]!));
    return map;
  }, [years]);

  // Hidden years (toggled off via the legend chips). Empty = all visible.
  const [hiddenYears, setHiddenYears] = useState<Set<string>>(new Set());
  const toggleYear = (year: string) => {
    setHiddenYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };
  const visibleYears = useMemo(
    () => years.filter((y) => !hiddenYears.has(y)),
    [years, hiddenYears],
  );

  // Month window on the shared Jan–Dec axis (1..12 inclusive). Lets the
  // reader trim the overlay to a season or run a fair year-to-date
  // comparison (e.g. Jan–Jun across every year) rather than always
  // seeing all twelve months. Purely client-side — no refetch.
  const [monthStart, setMonthStart] = useState(1);
  const [monthEnd, setMonthEnd] = useState(12);
  // Keep the window ordered: dragging one bound past the other pushes
  // the other along instead of producing an empty range.
  const setStart = (m: number) => {
    setMonthStart(m);
    if (m > monthEnd) setMonthEnd(m);
  };
  const setEnd = (m: number) => {
    setMonthEnd(m);
    if (m < monthStart) setMonthStart(m);
  };

  // The current (newest) year is the "hero" — bold line + gradient fill,
  // with prior years receding to thin reference lines.
  const heroYear = years[0] ?? '';
  const heroColor = colorByYear.get(heroYear) ?? colors.chartPrimary;

  // Latest month with data in the newest year — anchors the "YTD" preset
  // so year-to-date means Jan..current across every overlaid year.
  const latestHeroMonth = useMemo(() => {
    let found = 0;
    for (const p of data?.points ?? []) {
      const [y, m] = p.period.split('-');
      if (y === heroYear && m) found = Math.max(found, Number(m));
    }
    return found || 12;
  }, [data, heroYear]);

  const monthPresets: MonthPreset[] = [
    { label: 'Full Year', range: [1, 12] },
    { label: 'YTD', range: [1, latestHeroMonth] },
    { label: 'Q1', range: [1, 3] },
    { label: 'Q2', range: [4, 6] },
    { label: 'Q3', range: [7, 9] },
    { label: 'Q4', range: [10, 12] },
  ];
  const applyPreset = (range: [number, number]) => {
    setMonthStart(range[0]);
    setMonthEnd(range[1]);
  };

  // Pivot the flat monthly series into 12 month rows, one value per year.
  const monthRows = useMemo<MonthRow[]>(() => {
    const rows: MonthRow[] = MONTHS.map((m, i) => ({
      monthNum: i + 1,
      month: m,
      vol: {},
      accts: {},
    }));
    for (const y of years) {
      for (const r of rows) {
        r.vol[y] = null;
        r.accts[y] = null;
      }
    }
    for (const p of data?.points ?? []) {
      const [y, m] = p.period.split('-');
      const idx = m ? Number(m) - 1 : -1;
      if (!y || idx < 0 || idx > 11) continue;
      const row = rows[idx]!;
      row.vol[y] = Number(p.cases_9l);
      row.accts[y] = p.active_accounts;
    }
    // Trim to the selected month window. All years keep their values so
    // the tooltip's year-over-year delta still resolves; only the axis
    // extent narrows.
    return rows.filter((r) => r.monthNum >= monthStart && r.monthNum <= monthEnd);
  }, [data, years, monthStart, monthEnd]);

  // Summary stats over the *filtered* view — total, avg (active months),
  // peak, lowest (non-zero), avg accounts. Scoped to the selected years
  // and month window so the strip always describes exactly what the
  // chart is showing.
  const stats = useMemo(() => {
    const visible = new Set(visibleYears);
    const points = (data?.points ?? []).filter((p) => {
      const [y, m] = p.period.split('-');
      const mn = m ? Number(m) : 0;
      return !!y && visible.has(y) && mn >= monthStart && mn <= monthEnd;
    });
    if (points.length === 0) return null;
    const shaped = points.map((p) => {
      const [y, m] = p.period.split('-');
      return {
        cases_9l: Number(p.cases_9l),
        active_accounts: p.active_accounts,
        label: monthYearLabel(y ?? '', m ? Number(m) : 0),
      };
    });
    const totalVol = shaped.reduce((s, d) => s + d.cases_9l, 0);
    const activeMonths = shaped.filter((d) => d.cases_9l > 0);
    const avgVol = activeMonths.length > 0 ? totalVol / activeMonths.length : 0;
    const peak = shaped.reduce((best, d) => (d.cases_9l > best.cases_9l ? d : best), shaped[0]!);
    const lowest =
      activeMonths.length > 0
        ? activeMonths.reduce(
            (worst, d) => (d.cases_9l < worst.cases_9l ? d : worst),
            activeMonths[0]!,
          )
        : peak;
    const acctMonths = shaped.filter((d) => d.active_accounts > 0);
    const avgAccounts =
      acctMonths.length > 0
        ? Math.round(acctMonths.reduce((s, d) => s + d.active_accounts, 0) / acctMonths.length)
        : 0;
    return { totalVol, avgVol, peak, lowest, avgAccounts, monthsCount: points.length };
  }, [data, visibleYears, monthStart, monthEnd]);

  useSectionExport(() => {
    const points = data?.points ?? [];
    const yoyColumns: ExcelColumn<MonthRow>[] = [
      { header: 'Month', value: (r) => r.month, width: 8 },
      ...visibleYears.map(
        (y): ExcelColumn<MonthRow> => ({
          header: `${y} 9L`,
          value: (r) => r.vol[y] ?? null,
          format: 'decimal',
        }),
      ),
    ];
    const latest = points.reduce<string>((m, p) => (p.period > m ? p.period : m), '');
    const preset = monthPresets.find((p) => p.range[0] === monthStart && p.range[1] === monthEnd);
    const monthSpan = `${MONTHS[monthStart - 1] ?? ''}–${MONTHS[monthEnd - 1] ?? ''}`;
    const filters: ExportFilter[] = [
      // Only a host-supplied range narrows the rows; the default view spans everything.
      range?.from || range?.to
        ? { label: 'Date range', value: `${range.from ?? 'Earliest'} to ${range.to ?? 'latest'}` }
        : { kind: 'info', label: 'Period', value: 'All available data' },
      {
        label: 'Years (Year over Year sheet)',
        value: hiddenYears.size > 0 ? [...visibleYears].sort().join(', ') : 'All years',
      },
      {
        label: 'Hidden years (Year over Year sheet)',
        value: hiddenYears.size > 0 ? [...hiddenYears].sort().join(', ') : 'None',
      },
      {
        label: 'Months (Year over Year sheet)',
        value: preset ? `${preset.label} (${monthSpan})` : monthSpan,
      },
      { kind: 'info', label: 'Granularity', value: 'Monthly' },
      { kind: 'info', label: 'Data through', value: latest.slice(0, 7) },
    ];
    return {
      title: 'Monthly Volume Trend',
      filters,
      sheets: [
        excelSheet({
          name: 'Monthly',
          rows: points,
          columns: [
            { header: 'Month', value: (p) => isoDate(p.period), format: 'date' },
            { header: '9L Cases', value: (p) => num(p.cases_9l), format: 'decimal' },
            { header: 'Physical Cases', value: (p) => num(p.cases_physical), format: 'decimal' },
            { header: 'Active Accounts', value: (p) => p.active_accounts, format: 'int' },
          ],
        }),
        excelSheet({ name: 'Year over Year', rows: monthRows, columns: yoyColumns }),
      ],
    };
  }, exportable && !!data);

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${colors.error}40`,
          bgcolor: `${colors.error}05`,
        }}
      >
        <Typography color="error">Failed to load trend: {error.message}</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={
        compact
          ? { p: 0, bgcolor: 'transparent', boxShadow: 'none' }
          : { p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }
      }
    >
      {/* Header — section icon + title on the left. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 1.5,
          mb: compact ? 1.5 : 2.5,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        {!compact && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${colors.chartPrimary}14`,
              flexShrink: 0,
            }}
          >
            <ShowChartIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={compact ? 'subtitle2' : 'h6'}
            sx={{ fontWeight: compact ? 700 : 600 }}
          >
            {heading}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : years.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
          No data
        </Typography>
      ) : (
        <>
          {/* Filter bar — scope the overlay by year and by month window.
              Both act on the already-loaded series (no refetch); the
              summary tiles below recompute to match the selection. */}
          <DepletionsTrendFilterBar
            years={years}
            colorByYear={colorByYear}
            hiddenYears={hiddenYears}
            onToggleYear={toggleYear}
            monthStart={monthStart}
            monthEnd={monthEnd}
            onStartChange={setStart}
            onEndChange={setEnd}
            presets={monthPresets}
            onPreset={applyPreset}
          />

          {!stats ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
              No data for the selected years and months — widen the filters above.
            </Typography>
          ) : (
            <>
              {/* 5-tile summary strip — Total · Avg · Peak · Lowest · Avg Accounts. */}
              {!compact &&
                (() => {
                  const tiles: SummaryTile[] = [
                    {
                      label: 'Total Volume',
                      value: fmt6PkNumber(stats.totalVol),
                      sub: `6-pk across all months · ${numberFormatter.format(stats.totalVol)} 9L`,
                      color: colors.chartPrimary,
                      tipTitle: 'Total Volume',
                      tipBody: (
                        <>
                          Sum of every month&apos;s 6-pack equivalents in the dataset (
                          <strong>{stats.monthsCount} months</strong>).
                          <br />
                          <br />
                          This is a cumulative scale measure. Pair it with{' '}
                          <strong>Monthly Avg</strong> to see whether the total comes from a steady
                          drip or a few big spikes.
                        </>
                      ),
                    },
                    {
                      label: 'Monthly Avg',
                      value: fmt6PkNumber(stats.avgVol),
                      sub: '6-pk per active month',
                      color: colors.chartSecondary,
                      tipTitle: 'Monthly Avg',
                      tipBody: (
                        <>
                          Average 6-pack per <em>active</em> month — total volume divided by months
                          that actually moved product (zero-volume months excluded so the average
                          reflects real cadence).
                        </>
                      ),
                    },
                    {
                      label: 'Peak Month',
                      value: stats.peak.label,
                      sub: `${fmt6PkNumber(stats.peak.cases_9l)} 6-pk · ${numberFormatter.format(stats.peak.cases_9l)} 9L`,
                      color: colors.success,
                      tipTitle: 'Peak Month',
                      tipBody: (
                        <>
                          The single month with the highest 6-pack volume —{' '}
                          <strong>{stats.peak.label}</strong> at{' '}
                          <strong>{fmt6PkNumber(stats.peak.cases_9l)} 6-pk</strong>.
                        </>
                      ),
                    },
                    {
                      label: 'Lowest Month',
                      value: stats.lowest.label,
                      sub: `${fmt6PkNumber(stats.lowest.cases_9l)} 6-pk · ${numberFormatter.format(stats.lowest.cases_9l)} 9L`,
                      color: colors.warning,
                      tipTitle: 'Lowest Month',
                      tipBody: (
                        <>
                          The lowest <em>non-zero</em> month — <strong>{stats.lowest.label}</strong>{' '}
                          at <strong>{fmt6PkNumber(stats.lowest.cases_9l)} 6-pk</strong>. Months
                          with zero depletion are excluded.
                        </>
                      ),
                    },
                    {
                      label: 'Avg Accounts',
                      value: stats.avgAccounts.toLocaleString(),
                      sub: 'active per month',
                      color: '#8b5cf6',
                      tipTitle: 'Avg Accounts (active / month)',
                      tipBody: (
                        <>
                          Average unique accounts that moved any product per month. Measures
                          distribution <em>breadth</em>.
                        </>
                      ),
                    },
                  ];
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0,
                        mb: 3,
                        borderRadius: 2,
                        border: `1px solid ${colors.border}`,
                        overflow: 'hidden',
                      }}
                    >
                      {tiles.map((s, i, arr) => (
                        <Tooltip
                          key={s.label}
                          placement="top"
                          arrow
                          title={
                            <Box>
                              <Typography
                                sx={{ fontSize: 12, fontWeight: 700, color: s.color, mb: 0.5 }}
                              >
                                {s.tipTitle}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11.5,
                                  color: colors.textSecondary,
                                  lineHeight: 1.55,
                                }}
                                component="div"
                              >
                                {s.tipBody}
                              </Typography>
                            </Box>
                          }
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#fff',
                                border: `1px solid ${colors.border}`,
                                borderRadius: 1.5,
                                px: 2,
                                py: 1.5,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                                maxWidth: 320,
                              },
                            },
                            arrow: {
                              sx: {
                                color: '#fff',
                                '&::before': { border: `1px solid ${colors.border}` },
                              },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: { xs: '45%', sm: 'auto' },
                              py: 1.5,
                              px: 1.75,
                              textAlign: 'center',
                              borderRight:
                                i < arr.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
                              cursor: 'help',
                              transition: 'background-color 0.15s',
                              '&:hover': { bgcolor: colors.surfaceSubtle },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: colors.textMuted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                mb: 0.5,
                                borderBottom: `1px dotted ${colors.textMuted}40`,
                                display: 'inline-block',
                                paddingBottom: '1px',
                              }}
                            >
                              {s.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 17,
                                fontWeight: 700,
                                color: s.color,
                                lineHeight: 1.05,
                                letterSpacing: '-0.02em',
                              }}
                            >
                              {s.value}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: colors.textMuted, mt: 0.4 }}>
                              {s.sub}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  );
                })()}

              {/* Interactivity affordance — a clean hero line gives no hint
              it's hoverable, so spell it out. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.6,
                  mb: 1,
                  color: colors.textMuted,
                }}
              >
                <TouchAppOutlinedIcon sx={{ fontSize: 15 }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.01em' }}>
                  Hover any month for the year-over-year breakdown
                </Typography>
              </Box>

              {/* Volume overlay — hero current year (bold + gradient fill),
              prior years as thin muted reference lines on a shared
              Jan–Dec axis. A partial current year stops at its last
              month. */}
              <Box sx={{ mb: 1.5, cursor: 'crosshair' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart
                    data={monthRows}
                    margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
                  >
                    <defs>
                      <linearGradient id="heroVolFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={heroColor} stopOpacity={0.24} />
                        <stop offset="100%" stopColor={heroColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={colors.borderLight}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      tick={{ fontSize: 11, fill: colors.textMuted }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: colors.textMuted }}
                      axisLine={false}
                      tickLine={false}
                      width={56}
                      tickFormatter={(v) => numberFormatter.format((v as number) * 2)}
                      label={{
                        value: '6-pk equivalents',
                        angle: -90,
                        position: 'insideLeft',
                        offset: 12,
                        style: {
                          textAnchor: 'middle',
                          fontSize: 10,
                          fontWeight: 700,
                          fill: colors.textMuted,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        },
                      }}
                    />
                    <RTooltip
                      cursor={{
                        stroke: colors.chartPrimary,
                        strokeWidth: 1,
                        strokeDasharray: '3 3',
                      }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const row = payload[0]?.payload as MonthRow | undefined;
                        if (!row) return null;
                        return (
                          <YoYTooltip
                            monthLabel={String(label)}
                            row={row}
                            years={visibleYears}
                            colorByYear={colorByYear}
                            pick={(r, y) => r.vol[y] ?? null}
                            format={(v) => `${fmt6PkNumber(v)} 6-pk`}
                          />
                        );
                      }}
                    />
                    {/* Prior years — thin, muted reference lines. */}
                    {visibleYears
                      .filter((y) => y !== heroYear)
                      .map((y) => (
                        <Line
                          key={y}
                          type="monotone"
                          dataKey={(r: MonthRow) => r.vol[y]}
                          name={y}
                          stroke={colorByYear.get(y)}
                          strokeWidth={1.75}
                          strokeOpacity={0.5}
                          dot={false}
                          activeDot={{ r: 4, fill: colorByYear.get(y), strokeWidth: 0 }}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      ))}
                    {/* Hero current year — bold line + soft gradient fill,
                    drawn last so it sits on top. */}
                    {visibleYears.includes(heroYear) && (
                      <Area
                        type="monotone"
                        dataKey={(r: MonthRow) => r.vol[heroYear]}
                        name={heroYear}
                        stroke={heroColor}
                        strokeWidth={3.25}
                        fill="url(#heroVolFill)"
                        dot={false}
                        activeDot={{ r: 5, fill: heroColor, strokeWidth: 0 }}
                        connectNulls={false}
                        isAnimationActive={false}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>

              {/* Active-accounts overlay — same year-over-year treatment so the
              base movement reads on the same month axis as the volume.
              Hidden in the compact per-product variant. */}
              {!compact && (
                <Box sx={{ mb: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 0.5,
                      ml: 1,
                    }}
                  >
                    Active accounts by month
                  </Typography>
                  <Box sx={{ cursor: 'crosshair' }}>
                    <ResponsiveContainer width="100%" height={150}>
                      <ComposedChart
                        data={monthRows}
                        margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                      >
                        <defs>
                          <linearGradient id="heroAcctFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={heroColor} stopOpacity={0.22} />
                            <stop offset="100%" stopColor={heroColor} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={colors.borderLight}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={{ fontSize: 11, fill: colors.textMuted }}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: colors.textMuted }}
                          axisLine={false}
                          tickLine={false}
                          width={56}
                          label={{
                            value: 'Active accounts',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 12,
                            style: {
                              textAnchor: 'middle',
                              fontSize: 10,
                              fontWeight: 700,
                              fill: colors.textMuted,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                            },
                          }}
                        />
                        <RTooltip
                          cursor={{
                            stroke: colors.chartPrimary,
                            strokeWidth: 1,
                            strokeDasharray: '3 3',
                          }}
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const row = payload[0]?.payload as MonthRow | undefined;
                            if (!row) return null;
                            return (
                              <YoYTooltip
                                monthLabel={String(label)}
                                row={row}
                                years={visibleYears}
                                colorByYear={colorByYear}
                                pick={(r, y) => r.accts[y] ?? null}
                                format={(v) => `${v.toLocaleString()} accts`}
                              />
                            );
                          }}
                        />
                        {/* Prior years — thin, muted reference lines. */}
                        {visibleYears
                          .filter((y) => y !== heroYear)
                          .map((y) => (
                            <Line
                              key={y}
                              type="monotone"
                              dataKey={(r: MonthRow) => r.accts[y]}
                              name={y}
                              stroke={colorByYear.get(y)}
                              strokeWidth={1.75}
                              strokeOpacity={0.5}
                              dot={false}
                              activeDot={{ r: 4, fill: colorByYear.get(y), strokeWidth: 0 }}
                              connectNulls={false}
                              isAnimationActive={false}
                            />
                          ))}
                        {/* Hero current year — bold line + soft gradient fill. */}
                        {visibleYears.includes(heroYear) && (
                          <Area
                            type="monotone"
                            dataKey={(r: MonthRow) => r.accts[heroYear]}
                            name={heroYear}
                            stroke={heroColor}
                            strokeWidth={2.75}
                            fill="url(#heroAcctFill)"
                            dot={false}
                            activeDot={{ r: 4, fill: heroColor, strokeWidth: 0 }}
                            connectNulls={false}
                            isAnimationActive={false}
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}

              {/* Reader's guide — hidden in the compact per-product variant. */}
              {!compact && (
                <SectionExplainer
                  rows={[
                    {
                      sample: <TCSampleYearLines />,
                      term: 'One line per year',
                      desc: (
                        <>
                          Each calendar year is plotted over the same Jan–Dec axis, so the same
                          month sits directly above/below its counterparts in other years. The{' '}
                          <strong>current year</strong> is the hero — bold, with a soft gradient
                          fill — while prior years recede to thin reference lines.
                        </>
                      ),
                    },
                    {
                      sample: <TCSampleHoverCompare />,
                      term: 'Hover to compare',
                      desc: (
                        <>
                          Hover any month to see every visible year&apos;s value for that month,
                          with the <strong>year-over-year change</strong> versus the prior year —
                          the fastest way to spot whether this March beat last March.
                        </>
                      ),
                    },
                    {
                      sample: <TCSampleYearChip />,
                      term: 'Year & month filters',
                      desc: (
                        <>
                          In the filter bar, click a <strong>year</strong> chip to hide/show its
                          line — compare any subset, e.g. just the last two years — and set the{' '}
                          <strong>month</strong> window (or a preset like <em>YTD</em> or{' '}
                          <em>Q1–Q4</em>) to trim the axis to a season or year-to-date. The summary
                          tiles recompute to match the current selection.
                        </>
                      ),
                    },
                    {
                      sample: <TCSampleAccountsLine />,
                      term: 'Active accounts (bottom)',
                      desc: (
                        <>
                          The lower chart applies the same year-over-year overlay to how many
                          distinct accounts bought each month. When volume climbs but accounts stay
                          flat, <em>existing accounts are buying more</em>; when accounts climb in
                          step, <em>the base is growing</em>.
                        </>
                      ),
                    },
                  ]}
                />
              )}
            </>
          )}
        </>
      )}
    </Paper>
  );
}

// ----------------------------------------------------------------
// Filter bar — year chips (hide/show a year's line) plus a month
// window (start–end selects + quick presets) that trims the shared
// Jan–Dec axis. All controls act on the already-loaded series.
// ----------------------------------------------------------------

interface MonthPreset {
  label: string;
  range: [number, number];
}

interface DepletionsTrendFilterBarProps {
  years: string[];
  colorByYear: Map<string, string>;
  hiddenYears: Set<string>;
  onToggleYear: (year: string) => void;
  monthStart: number;
  monthEnd: number;
  onStartChange: (month: number) => void;
  onEndChange: (month: number) => void;
  presets: MonthPreset[];
  onPreset: (range: [number, number]) => void;
}

function DepletionsTrendFilterBar({
  years,
  colorByYear,
  hiddenYears,
  onToggleYear,
  monthStart,
  monthEnd,
  onStartChange,
  onEndChange,
  presets,
  onPreset,
}: DepletionsTrendFilterBarProps) {
  const [helpAnchor, setHelpAnchor] = useState<HTMLButtonElement | null>(null);
  const activePreset =
    presets.find((p) => p.range[0] === monthStart && p.range[1] === monthEnd)?.label ?? null;
  return (
    <Box
      sx={{
        mb: 3,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: `1px solid ${colors.borderLight}`,
        bgcolor: colors.surfaceSubtle,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      {/* Header — section label + on-demand "how to filter" guide. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 16, color: colors.textMuted }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Filters
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={(e) => setHelpAnchor(e.currentTarget)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.4,
            border: `1px solid ${helpAnchor ? `${colors.chartPrimary}55` : colors.border}`,
            borderRadius: 999,
            bgcolor: helpAnchor ? `${colors.chartPrimary}0a` : '#fff',
            color: helpAnchor ? colors.chartPrimary : colors.textMuted,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.02em',
            cursor: 'pointer',
            transition: 'color 0.15s, background 0.15s, border-color 0.15s',
            '&:hover': {
              color: colors.chartPrimary,
              borderColor: `${colors.chartPrimary}66`,
              bgcolor: `${colors.chartPrimary}0a`,
            },
          }}
        >
          <HelpOutlineOutlinedIcon sx={{ fontSize: 14 }} />
          How to filter
        </Box>
      </Box>

      {/* Controls — years + month window. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* Years */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FilterLabel>Years</FilterLabel>
          {years.map((y) => {
            const color = colorByYear.get(y)!;
            const hidden = hiddenYears.has(y);
            return (
              <Chip
                key={y}
                label={y}
                size="small"
                onClick={() => onToggleYear(y)}
                icon={
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: hidden ? 'transparent' : color,
                      border: `2px solid ${color}`,
                      ml: 0.75,
                    }}
                  />
                }
                sx={{
                  height: 26,
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  color: hidden ? colors.textMuted : color,
                  bgcolor: hidden ? 'transparent' : `${color}12`,
                  border: `1px solid ${hidden ? colors.borderLight : `${color}55`}`,
                  opacity: hidden ? 0.6 : 1,
                  '& .MuiChip-label': { px: 0.9 },
                  '&:hover': { bgcolor: hidden ? colors.surfaceSubtle : `${color}1f` },
                }}
              />
            );
          })}
        </Box>

        {/* Month window + presets */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FilterLabel>Months</FilterLabel>
          <MonthSelect value={monthStart} onChange={onStartChange} />
          <Typography sx={{ color: colors.textMuted, fontWeight: 700, mx: 0.25 }}>–</Typography>
          <MonthSelect value={monthEnd} onChange={onEndChange} />
          <Box sx={{ display: 'flex', gap: 0.5, ml: 0.5, flexWrap: 'wrap' }}>
            {presets.map((p) => {
              const active = activePreset === p.label;
              return (
                <Chip
                  key={p.label}
                  label={p.label}
                  size="small"
                  onClick={() => onPreset(p.range)}
                  sx={{
                    height: 26,
                    fontWeight: 700,
                    fontSize: 11.5,
                    cursor: 'pointer',
                    color: active ? '#fff' : colors.textSecondary,
                    bgcolor: active ? colors.chartPrimary : '#fff',
                    border: `1px solid ${active ? colors.chartPrimary : colors.border}`,
                    '& .MuiChip-label': { px: 1 },
                    '&:hover': {
                      bgcolor: active ? colors.chartPrimary : colors.surfaceSubtle,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      <FilterHelpPopover anchorEl={helpAnchor} onClose={() => setHelpAnchor(null)} />
    </Box>
  );
}

// Structured "how to filter" reference — opened from the filter bar's
// help link. One row per control: a sample swatch, the control's name,
// and a one-line description of what it does.
function FilterHelpPopover({
  anchorEl,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            p: 2,
            maxWidth: 340,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
            boxShadow: '0 12px 32px rgba(15,23,42,0.12)',
          },
        },
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.25 }}>
        How to filter this view
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: colors.textMuted, mb: 1.5 }}>
        Every control acts on the loaded data instantly — nothing reloads.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <FilterHelpRow
          swatch={
            <Box sx={{ display: 'flex', gap: 0.4 }}>
              <FilterHelpDot color={YEAR_COLORS[0]!} />
              <FilterHelpDot color={YEAR_COLORS[1]!} />
            </Box>
          }
          term="Years"
          desc={
            <>
              Click a year chip to show or hide its line — compare any subset, e.g. just{' '}
              <strong>2025 vs 2026</strong>.
            </>
          }
        />
        <FilterHelpRow
          swatch={<FilterHelpPill>Jan–Dec</FilterHelpPill>}
          term="Month window"
          desc={
            <>
              Set the <strong>start</strong> and <strong>end</strong> months to focus on a season or
              a partial year.
            </>
          }
        />
        <FilterHelpRow
          swatch={<FilterHelpPill filled>Q1</FilterHelpPill>}
          term="Quick presets"
          desc={
            <>
              One click for <strong>Full Year</strong>, <strong>YTD</strong> (Jan–current), or a
              quarter <strong>Q1–Q4</strong>.
            </>
          }
        />
        <FilterHelpRow
          swatch={<AutorenewIcon sx={{ fontSize: 18, color: colors.success }} />}
          term="Live totals"
          desc={<>The summary tiles above recompute to match your current selection.</>}
        />
      </Box>
    </Popover>
  );
}

function FilterHelpDot({ color }: { color: string }) {
  return <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />;
}

function FilterHelpPill({ children, filled }: { children: ReactNode; filled?: boolean }) {
  return (
    <Box
      sx={{
        px: 0.7,
        py: 0.15,
        borderRadius: 1,
        fontSize: 10,
        fontWeight: 800,
        whiteSpace: 'nowrap',
        color: filled ? '#fff' : colors.textSecondary,
        bgcolor: filled ? colors.chartPrimary : '#fff',
        border: `1px solid ${filled ? colors.chartPrimary : colors.border}`,
      }}
    >
      {children}
    </Box>
  );
}

function FilterHelpRow({
  swatch,
  term,
  desc,
}: {
  swatch: ReactNode;
  term: string;
  desc: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Box sx={{ width: 46, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: 0.25 }}>
        {swatch}
      </Box>
      <Box>
        <Typography
          sx={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.3 }}
        >
          {term}
        </Typography>
        <Typography
          sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.45 }}
          component="div"
        >
          {desc}
        </Typography>
      </Box>
    </Box>
  );
}

function FilterLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        mr: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

function MonthSelect({ value, onChange }: { value: number; onChange: (month: number) => void }) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      size="small"
      MenuProps={{ MenuListProps: { dense: true } }}
      sx={{
        height: 30,
        fontSize: 12.5,
        fontWeight: 700,
        color: colors.textPrimary,
        bgcolor: '#fff',
        '& .MuiSelect-select': { py: 0.25, pl: 1.25, pr: '26px !important' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
      }}
    >
      {MONTHS.map((m, i) => (
        <MenuItem key={m} value={i + 1} sx={{ fontSize: 12.5, fontWeight: 600 }}>
          {m}
        </MenuItem>
      ))}
    </Select>
  );
}

interface SummaryTile {
  label: string;
  value: string;
  sub: string;
  color: string;
  tipTitle: string;
  tipBody: ReactNode;
}

// ----------------------------------------------------------------
// Shared year-over-year tooltip — lists every visible year's value for
// the hovered month, newest first, with the change versus the prior
// year so the reader can eyeball momentum without mental math.
// ----------------------------------------------------------------

interface YoYTooltipProps {
  monthLabel: string;
  row: MonthRow;
  years: string[];
  colorByYear: Map<string, string>;
  pick: (row: MonthRow, year: string) => number | null;
  format: (value: number) => string;
}

function YoYTooltip({ monthLabel, row, years, colorByYear, pick, format }: YoYTooltipProps) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${colors.border}`,
        borderRadius: 1.5,
        p: 1.5,
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
        minWidth: 200,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>{monthLabel}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
        {years.map((y) => {
          const v = pick(row, y);
          if (v === null) return null;
          // Prior calendar year's value for this same month (YoY delta).
          const prior = pick(row, String(Number(y) - 1));
          const pct = prior !== null && prior > 0 ? Math.round(((v - prior) / prior) * 100) : null;
          const color = colorByYear.get(y) ?? colors.textPrimary;
          return (
            <Box
              key={y}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color }}>{y}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary }}>
                  {format(v)}
                </Typography>
                {pct !== null && (
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: pct >= 0 ? colors.success : colors.error,
                    }}
                  >
                    {pct >= 0 ? '+' : ''}
                    {pct}%
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography sx={{ fontSize: 10, color: colors.textMuted, mt: 0.75, fontStyle: 'italic' }}>
        % is vs the same month a year earlier.
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------
// Reader's-guide sample swatches
// ----------------------------------------------------------------

function TCSampleYearLines() {
  return (
    <svg width={48} height={22} viewBox="0 0 48 22">
      <defs>
        <linearGradient id="tc-hero" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.3} />
          <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d="M 2 15 Q 14 13 24 9 T 46 3 L 46 22 L 2 22 Z" fill="url(#tc-hero)" stroke="none" />
      <path
        d="M 2 20 Q 14 19 24 16 T 46 13"
        stroke="#0d9488"
        strokeWidth={1.3}
        fill="none"
        opacity={0.5}
      />
      <path d="M 2 15 Q 14 13 24 9 T 46 3" stroke="#4f46e5" strokeWidth={2.4} fill="none" />
    </svg>
  );
}

function TCSampleHoverCompare() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: '2px',
        px: 0.6,
        py: 0.3,
        bgcolor: '#fff',
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4f46e5' }} />
        <Box sx={{ width: 18, height: 3, bgcolor: colors.borderLight, borderRadius: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0d9488' }} />
        <Box sx={{ width: 18, height: 3, bgcolor: colors.borderLight, borderRadius: 1 }} />
      </Box>
    </Box>
  );
}

function TCSampleYearChip() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        px: 0.7,
        py: 0.2,
        bgcolor: '#4f46e512',
        border: '1px solid #4f46e555',
        borderRadius: 1,
        fontSize: 10,
        fontWeight: 700,
        color: '#4f46e5',
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#4f46e5' }} />
      2026
    </Box>
  );
}

function TCSampleAccountsLine() {
  return (
    <svg width={48} height={22} viewBox="0 0 48 22">
      <path
        d="M 2 18 L 12 12 L 22 15 L 32 8 L 46 10"
        stroke="#4f46e5"
        strokeWidth={1.6}
        fill="none"
      />
    </svg>
  );
}
